/**
 * The Horizon Rule sandbox — pure calculation functions for the public
 * landing page's inline interactive preview (Amilli — Public Landing and
 * Preview Specification §5). Entirely fictional sample data: no real user,
 * account, or financial figures are read or written anywhere in this module.
 *
 * Kept dependency-free and side-effect-free on purpose so it can run in a
 * plain `bun test` without a DOM, a database, or a session — landing.js owns
 * all rendering and DOM binding, this file owns only the math.
 */

/** "Alex" — 23-year-old barista & retail sales associate. Fictional. */
export const SANDBOX_PERSONA = {
  name: 'Alex',
  hourlyWage: 18.50,
  hoursLoggedSoFar: 24,     // already-worked shifts this pay period, fixed
  minHours: 0,               // slider floor — reachable to exercise the zero/empty state
  maxHours: 80,
  defaultHours: 64,
  minCommission: 0,
  maxCommission: 800,
  defaultCommission: 200,
  obligations: [
    { label: 'Rent contribution', amount: 600, dueInDays: 6 },
    { label: 'Car insurance',     amount: 145, dueInDays: 11 },
    { label: 'Phone bill',        amount: 65,  dueInDays: 13 },
  ],
  milestone: { label: 'Emergency Buffer', target: 5000, current: 3200 },
};

/** Est. FICA/withholding rate, clearly labeled everywhere it's surfaced as an estimate. */
export const ESTIMATED_TAX_RATE = 0.185;

export function totalObligations(obligations) {
  return obligations.reduce((sum, o) => sum + o.amount, 0);
}

/**
 * Runs the sandbox's whole "shift → paycheck → cushion → milestone" chain.
 *
 * `hoursScheduled <= 0` (or not a finite number) returns the zero/cleared
 * state — the spec is explicit that this must never render `$0` as if it
 * were a real conclusion, so no financial figures are computed at all in
 * that branch, only `{ state: 'empty' }`.
 *
 * @param {{
 *   hoursScheduled: number,
 *   hourlyWage?: number,
 *   commission?: number,
 *   obligations?: { label: string, amount: number, dueInDays: number }[],
 *   milestone?: { label: string, target: number, current: number },
 *   hoursLoggedSoFar?: number,
 *   taxRate?: number,
 * }} args
 * @returns {{ state: 'empty' } | {
 *   state: 'positive' | 'tight' | 'deficit',
 *   actualHours: number,
 *   hoursScheduled: number,
 *   grossPay: number,
 *   estimatedNet: number,
 *   totalObligations: number,
 *   cushion: number,
 *   milestoneRemaining: number,
 *   milestoneDeltaPct: number,
 *   payPeriodsToMilestone: number | null,
 *   deficitHoursNeeded: number | null,
 * }}
 */
export function calculateHorizon({
  hoursScheduled,
  hourlyWage = SANDBOX_PERSONA.hourlyWage,
  commission = 0,
  obligations = SANDBOX_PERSONA.obligations,
  milestone = SANDBOX_PERSONA.milestone,
  hoursLoggedSoFar = SANDBOX_PERSONA.hoursLoggedSoFar,
  taxRate = ESTIMATED_TAX_RATE,
} = {}) {
  if (!Number.isFinite(hoursScheduled) || hoursScheduled <= 0) {
    return { state: 'empty' };
  }

  const actualHours = Math.min(hoursLoggedSoFar, hoursScheduled);
  const grossPay = hoursScheduled * hourlyWage + commission;
  const estimatedNet = grossPay * (1 - taxRate);
  const totalObl = totalObligations(obligations);
  const cushion = estimatedNet - totalObl;

  let state;
  if (cushion < 0) state = 'deficit';
  else if (cushion <= 50) state = 'tight';
  else state = 'positive';

  const milestoneRemaining = Math.max(milestone.target - milestone.current, 0);
  const milestoneDeltaPct = milestoneRemaining > 0
    ? Math.min((Math.max(cushion, 0) / milestoneRemaining) * 100, 100)
    : 100;

  // Two-week pay periods until the milestone is reached at this cushion rate.
  // null when the cushion can't make progress (deficit/zero) — the caller
  // must render that as "reach a positive cushion first," not a number.
  const payPeriodsToMilestone = cushion > 0 && milestoneRemaining > 0
    ? Math.ceil(milestoneRemaining / cushion)
    : cushion >= milestoneRemaining && milestoneRemaining > 0 ? 1 : null;

  // Deficit-only: additional scheduled hours (beyond what's already entered)
  // that would bring the cushion back to exactly zero, holding wage and
  // commission fixed. Purely mathematical, never framed as a judgment.
  let deficitHoursNeeded = null;
  if (state === 'deficit') {
    const neededGross = totalObl / (1 - taxRate);
    const neededHours = (neededGross - commission) / hourlyWage;
    deficitHoursNeeded = Math.max(Math.ceil(neededHours - hoursScheduled), 0);
  }

  return {
    state,
    actualHours,
    hoursScheduled,
    grossPay,
    estimatedNet,
    totalObligations: totalObl,
    cushion,
    milestoneRemaining,
    milestoneDeltaPct,
    payPeriodsToMilestone,
    deficitHoursNeeded,
  };
}

export const STATE_COPY = {
  empty:    'Enter expected hours to calculate your next pay period.',
  positive: 'Safe surplus for savings or living',
  tight:    'Covers bills with minimal margin',
  // deficitHoursNeeded is interpolated by the caller — kept out of this
  // string so the copy stays purely mathematical, per the spec's "never
  // judgmental" state-handling note.
  deficit:  'Shortfall projected',
};
